import { createContext, useContext, useLayoutEffect, useRef, type ReactNode } from 'react'
import { useSettings, getThemeForArea, type ThemeMode, type PublicSettings } from './useSettings'

interface SettingsContextValue {
  settings: PublicSettings
  loading: boolean
  saving: boolean
  updateSetting: <K extends keyof PublicSettings>(key: K, value: PublicSettings[K]) => Promise<void>
  updateSettings: (updates: Partial<PublicSettings>) => Promise<void>
  resetSettings: () => Promise<void>
  getTheme: (area: 'drive' | 'workspace' | 'chat') => ThemeMode
}

const SettingsContext = createContext<SettingsContextValue | null>(null)

/** Last-resort document background if the active theme's `--bg-primary` can't be
 *  resolved. Mirrors index.css's own pre-theme fallback — this is the ONE place
 *  a colour is still hardcoded, and only for the case where the stylesheet that
 *  owns the real values isn't there to ask. */
const FALLBACK_BG = '#09090b'

/** localStorage key the pre-paint script in apps/web/index.html reads. Kept as a
 *  named constant so the two ends of that contract are greppable. */
const THEME_BG_CACHE_KEY = 'papan:theme-bg'

export function ThemeProvider({ children }: { children: ReactNode }) {
  const { settings, loading, saving, updateSetting, updateSettings, resetSettings } = useSettings()

  const getTheme = (area: 'drive' | 'workspace' | 'chat'): ThemeMode => {
    return getThemeForArea(settings, area)
  }

  // Suppress ALL transitions for the single frame a theme switch lands, then
  // restore them. Elements that transition background-color/color (the theme
  // toggle, active taskbar pill, drive nav item, chat dropdown buttons, …)
  // otherwise animate their resolved color across the theme jump and flash —
  // while the un-transitioned surfaces around them snap instantly, so the two
  // never line up. Killing transitions during the switch makes everything snap
  // together; removing the override on the next tick keeps hover transitions
  // intact. (Same technique as next-themes.) useLayoutEffect + a forced reflow
  // guarantee the override is in place before the browser paints the new theme.
  // Keep the DOCUMENT background + the browser UI in sync with the app theme:
  // the page backdrop (behind the app / any overscroll) and, crucially, the
  // mobile browser chrome — Safari's toolbar and Android's address bar follow
  // the `theme-color` meta — so the chrome tracks light / dim / dark instead
  // of being stuck on one color.
  //
  // The colour is READ FROM THE THEME'S OWN `--bg-primary` rather than a
  // hand-maintained map. The map used to be duplicated here and had to be
  // "kept in sync" with index.css by hand — a silent drift hazard, since
  // nothing failed when they diverged, the backdrop just stopped matching the
  // app. Now index.css is the single source of truth.
  const docTheme = settings.theme.workspace
  useLayoutEffect(() => {
    // Probe the theme class off-screen to resolve its --bg-primary. Cheap: it
    // runs once per theme change, not per render.
    const probe = document.createElement('div')
    probe.className = getThemeClass(docTheme)
    probe.style.display = 'none'
    document.body.appendChild(probe)
    const resolved = getComputedStyle(probe).getPropertyValue('--bg-primary').trim()
    probe.remove()
    // ALWAYS produce a colour. The old hardcoded map ended in `?? THEME_BG.dark`,
    // so it could not fail; a bare `if (!resolved) return` would silently leave
    // the document background, the theme-color meta AND the pre-paint cache
    // unset — a light/dim user would be pinned to the stylesheet's dark #09090b
    // on every load, with nothing failing loudly. `--bg-primary` can come back
    // empty if the stylesheet hasn't applied when this layout effect runs, or if
    // a consumer of this provider doesn't ship apps/web/src/index.css (it lives
    // in shared/core-ui, so that is not hypothetical).
    const bg = resolved || FALLBACK_BG

    document.documentElement.style.backgroundColor = bg
    document.body.style.backgroundColor = bg
    let meta = document.querySelector('meta[name="theme-color"]')
    if (!meta) {
      meta = document.createElement('meta')
      meta.setAttribute('name', 'theme-color')
      document.head.appendChild(meta)
    }
    meta.setAttribute('content', bg)
    // Cache the RESOLVED colour for the pre-paint script in index.html. Settings
    // come from the API, so on the next load React can't know the theme until
    // after first paint — without this, a light/dim user gets a frame of dark.
    try { localStorage.setItem(THEME_BG_CACHE_KEY, bg) } catch { /* storage blocked */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docTheme])

  const themeKey = `${settings.theme.drive}|${settings.theme.workspace}|${settings.theme.chat}`
  const firstRun = useRef(true)
  useLayoutEffect(() => {
    if (firstRun.current) { firstRun.current = false; return }
    const style = document.createElement('style')
    style.appendChild(document.createTextNode('*,*::before,*::after{transition:none!important}'))
    document.head.appendChild(style)
    // Force a synchronous style recalc so the new-theme colors are committed
    // with transitions disabled (no animation is armed).
    void window.getComputedStyle(document.body).opacity
    // Restore transitions after this paint so subsequent hovers still animate.
    const timer = window.setTimeout(() => { style.remove() }, 0)
    return () => { window.clearTimeout(timer); style.remove() }
  }, [themeKey])

  return (
    <SettingsContext.Provider value={{
      settings,
      loading,
      saving,
      updateSetting,
      updateSettings,
      resetSettings,
      getTheme
    }}>
      {children}
    </SettingsContext.Provider>
  )
}

/**
 * Hook to access settings and theme from context
 */
export function useTheme() {
  const context = useContext(SettingsContext)
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}

/**
 * The theme if a provider is present, otherwise `null`.
 *
 * `useTheme` THROWS without a provider, which is right for anything whose
 * appearance depends on the theme — failing loudly beats rendering unreadable
 * chrome. It is wrong for a purely decorative accent, where the throw turns a
 * missing provider into a blank screen instead of a slightly-off colour.
 * `MessageList` hit exactly that: reading the theme to tint one 20px canvas
 * made a ThemeProvider a hard requirement for rendering the whole transcript,
 * and took eight tests down with it.
 *
 * Use this ONLY where a sensible default exists; reach for `useTheme` otherwise.
 */
export function useThemeOptional() {
  return useContext(SettingsContext)
}

/**
 * Get the CSS class name for a theme
 */
export function getThemeClass(theme: ThemeMode): string {
  return `theme-${theme}`
}

interface ThemedAreaProps {
  area: 'drive' | 'workspace' | 'chat'
  children: ReactNode
  className?: string
  style?: React.CSSProperties
}

/**
 * Wrapper component that applies the theme for a specific area
 *
 * Forwards every other div prop. It used to destructure exactly four and render
 * none of the rest, which dropped anything else SILENTLY: `aria-*` and `data-*`
 * are exempt from JSX excess-property checks, so `<ThemedArea aria-busy="true">`
 * type-checked, read correctly, and never reached the DOM. The workspace loading
 * skeleton shipped that way — announcing nothing to a screen reader — and its
 * test passed regardless, because the test's own mock spread `{...rest}` while
 * the real component did not.
 */
export function ThemedArea({ area, children, className = '', style, ...rest }: ThemedAreaProps & Omit<React.ComponentPropsWithoutRef<'div'>, 'className' | 'style' | 'children'>) {
  const { getTheme } = useTheme()
  const theme = getTheme(area)
  const themeClass = getThemeClass(theme)

  return (
    <div {...rest} className={`${themeClass} ${className}`} style={style}>
      {children}
    </div>
  )
}
